import React, { useState, useEffect } from 'react';
import Button from './Button';
import TextInput from './TextInput';
import Checkbox from './Checkbox';
import CountrySelect from './CountrySelect';
import { useAuth } from '../contexts/AuthContext';

const RegisterForm = ({ editData, onClearEdit }) => {
  const { authenticatedFetch } = useAuth();
  const [formData, setFormData] = useState({
    active: true,
    applyFrom: '',
    applyTo: '',
    receiverName: '',
    receiverCity: '',
    receiverZip: '',
    receiverCtry: 'HU',
    rNameOn: false,
    rCityOn: false,
    rZipOn: false,
    rCtryOn: false,
    receiverStreet: '',
    rStreetOn: false,
    reference: '',
    email: '',
    phone: ''
  });

  const [message, setMessage] = useState({ text: '', type: '', visible: false });
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    if (editData) {
      // Populate form with edit data
      setFormData({
        active: editData.Active || false,
        applyFrom: editData.ApplyFrom ? new Date(editData.ApplyFrom).toISOString().split('T')[0] : '',
        applyTo: editData.ApplyTo ? new Date(editData.ApplyTo).toISOString().split('T')[0] : '',
        receiverName: editData.ReceiverName || '',
        receiverCity: editData.ReceiverCity || '',
        receiverZip: editData.ReceiverZip || '',
        receiverCtry: editData.ReceiverCtry || 'HU',
        rNameOn: editData.RNameON || false,
        rCityOn: editData.RCityOn || false,
        rZipOn: editData.RZipOn || false,
        rCtryOn: editData.RCtryOn || false,
        receiverStreet: editData.ReceiverStreet || '',
        rStreetOn: editData.RStreetOn || false,
        reference: editData.Reference || '',
        email: editData.Email || '',
        phone: editData.Phone || ''
      });
      setIsEditMode(true);
      setMessage({ 
        text: `Editing search criteria (ID: ${editData.ID})`, 
        type: 'info', 
        visible: true 
      });
    } else {
      // Set default dates for new records
      const today = new Date();
      const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, today.getDate());
      
      setFormData(prev => ({
        ...prev,
        applyFrom: today.toISOString().split('T')[0],
        applyTo: nextMonth.toISOString().split('T')[0]
      }));
      setIsEditMode(false);
    }
  }, [editData]);

  const handleInputChange = (e) => {
    // Add defensive check for e.target
    if (!e || !e.target) {
      console.error('Event or event.target is undefined in handleInputChange');
      return;
    }
    
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Auto-enable filters when user types in corresponding fields
    const fieldMappings = {
      'receiverName': 'rNameOn',
      'receiverCity': 'rCityOn',
      'receiverZip': 'rZipOn',
      'receiverCtry': 'rCtryOn',
      'receiverStreet': 'rStreetOn'
    };

    if (fieldMappings[name] && value.trim() !== '') {
      setFormData(prev => ({
        ...prev,
        [fieldMappings[name]]: true
      }));
    }
  };

  const showMessage = (text, type) => {
    setMessage({ text, type, visible: true });
    
    if (type === 'success' || type === 'info') {
      setTimeout(() => {
        setMessage(prev => ({ ...prev, visible: false }));
      }, 5000);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate date range
    if (formData.applyFrom && formData.applyTo) {
      const fromDate = new Date(formData.applyFrom);
      const toDate = new Date(formData.applyTo);
      
      if (fromDate >= toDate) {
        showMessage('Error: "Apply To" date must be after "Apply From" date.', 'error');
        return;
      }
    }

    try {
      const actionText = isEditMode ? 'Updating' : 'Registering';
      showMessage(`${actionText} search criteria...`, 'info');
      
      let url, method;
      if (isEditMode && editData?.ID) {
        // Update existing record
        url = `/api/register-search/${editData.ID}`;
        method = 'PUT';
        console.log('UPDATE mode - URL:', url, 'ID:', editData.ID);
      } else {
        // Create new record
        url = '/api/register-search';
        method = 'POST';
        console.log('CREATE mode - URL:', url);
      }
      
      console.log('Form data being sent:', formData);
      console.log('Request method:', method);
      
      const response = await authenticatedFetch(url, {
        method: method,
        body: JSON.stringify(formData)
      });
      
      const result = await response.json();
      
      if (result.success) {
        const successText = isEditMode ? 'Search criteria updated successfully!' : 'Search criteria registered successfully!';
        showMessage(successText, 'success');
        
        if (isEditMode) {
          // In edit mode, don't reset the form, just show success message
          // The user can click "New Search" if they want to create a new record
        } else {
          // Reset form but keep default dates for new registrations
          const today = new Date();
          const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, today.getDate());
          
          setFormData({
            active: true,
            applyFrom: today.toISOString().split('T')[0],
            applyTo: nextMonth.toISOString().split('T')[0],
            receiverName: '',
            receiverCity: '',
            receiverZip: '',
            receiverCtry: '',
            rNameOn: false,
            rCityOn: false,
            rZipOn: false,
            rCtryOn: false,
            receiverStreet: '',
            rStreetOn: false,
            reference: '',
            email: '',
            phone: ''
          });
        }
      } else {
        showMessage('Error: ' + result.message, 'error');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      const errorText = isEditMode ? 'Failed to update search criteria' : 'Failed to register search criteria';
      showMessage(`Error: ${errorText}. Please try again.`, 'error');
    }
  };

  const handleReset = () => {
    const today = new Date();
    const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, today.getDate());
    
    setFormData({
      active: true,
      applyFrom: today.toISOString().split('T')[0],
      applyTo: nextMonth.toISOString().split('T')[0],
      receiverName: '',
      receiverCity: '',
      receiverZip: '',
      receiverCtry: 'HU',
      rNameOn: false,
      rCityOn: false,
      rZipOn: false,
      rCtryOn: false,
      receiverStreet: '',
      rStreetOn: false,
      reference: '',
      email: '',
      phone: ''
    });
    setMessage({ text: '', type: '', visible: false });
  };

  const handleNewSearch = () => {
    if (onClearEdit) {
      onClearEdit();
    }
    handleReset();
  };

  return (
    <div className="tab-content active">
      <form onSubmit={handleSubmit} className="search-form">
        <div className="form-group">
          <Checkbox
            id="active"
            name="active"
            label="Active (Subject to Search)"
            checked={formData.active}
            onChange={(e) => handleInputChange(e)}
          />
        </div>

        <div className="form-row date-row">
          <div className="form-group">
            <label htmlFor="applyFrom">Apply From Date:</label>
            <input 
              type="date" 
              name="applyFrom" 
              value={formData.applyFrom}
              onChange={handleInputChange}
              required 
            />
          </div>
          <div className="form-group">
            <label htmlFor="applyTo">Apply To Date:</label>
            <input 
              type="date" 
              name="applyTo" 
              value={formData.applyTo}
              onChange={handleInputChange}
              required 
            />
          </div>
        </div>

        <div className="form-group">
          <div className="text-input-group">
            <label htmlFor="reference" className="text-input-label" style={{ fontWeight: 'bold' }}>
              Reference (Case Number)
            </label>
            <input
              id="reference"
              name="reference"
              type="text"
              value={formData.reference}
              onChange={handleInputChange}
              placeholder="Enter case number or reference"
              className="text-input"
            />
          </div>
        </div>

        <fieldset className="receiver-info">
          <legend>Receiver Information</legend>
          
          <div className="form-row">
            <div className="form-group">
              <TextInput
                id="receiverName"
                name="receiverName"
                label="Receiver Name"
                value={formData.receiverName}
                onChange={(e) => handleInputChange(e)}
                placeholder="Enter receiver name"
              />
            </div>
            <div className="form-group checkbox-group">
              <Checkbox
                id="rNameOn"
                name="rNameOn"
                label="Apply Name Filter"
                checked={formData.rNameOn}
                onChange={(e) => handleInputChange(e)}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <TextInput
                id="receiverCity"
                name="receiverCity"
                label="Receiver City"
                value={formData.receiverCity}
                onChange={(e) => handleInputChange(e)}
                placeholder="Enter city"
              />
            </div>
            <div className="form-group checkbox-group">
              <Checkbox
                id="rCityOn"
                name="rCityOn"
                label="Apply City Filter"
                checked={formData.rCityOn}
                onChange={(e) => handleInputChange(e)}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <TextInput
                id="receiverZip"
                name="receiverZip"
                label="Receiver ZIP"
                value={formData.receiverZip}
                onChange={(e) => handleInputChange(e)}
                placeholder="Enter postal code"
              />
            </div>
            <div className="form-group checkbox-group">
              <Checkbox
                id="rZipOn"
                name="rZipOn"
                label="Apply ZIP Filter"
                checked={formData.rZipOn}
                onChange={(e) => handleInputChange(e)}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <CountrySelect
                id="receiverCtry"
                name="receiverCtry"
                label="Receiver Country"
                value={formData.receiverCtry}
                onChange={(e) => handleInputChange(e)}
              />
            </div>
            <div className="form-group checkbox-group">
              <Checkbox
                id="rCtryOn"
                name="rCtryOn"
                label="Apply Country Filter"
                checked={formData.rCtryOn}
                onChange={(e) => handleInputChange(e)}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <TextInput
                id="receiverStreet"
                name="receiverStreet"
                label="Receiver Street"
                value={formData.receiverStreet}
                onChange={(e) => handleInputChange(e)}
                placeholder="Enter street address"
              />
            </div>
            <div className="form-group checkbox-group">
              <Checkbox
                id="rStreetOn"
                name="rStreetOn"
                label="Apply Street Filter"
                checked={formData.rStreetOn}
                onChange={(e) => handleInputChange(e)}
              />
            </div>
          </div>
        </fieldset>

        <fieldset className="contact-info">
          <legend>Contact Information</legend>
          
          <div className="form-row">
            <div className="form-group">
              <TextInput
                id="email"
                name="email"
                label="Email Address"
                value={formData.email}
                onChange={(e) => handleInputChange(e)}
                placeholder="Enter email address"
                type="email"
              />
            </div>
            <div className="form-group">
              <TextInput
                id="phone"
                name="phone"
                label="Phone Number"
                value={formData.phone}
                onChange={(e) => handleInputChange(e)}
                placeholder="Enter phone number"
                type="tel"
              />
            </div>
          </div>
        </fieldset>

        <div className="form-actions">
          <Button 
            type="submit" 
            variant="primary"
            size="lg"
            label={isEditMode ? "Update Search Criteria" : "Register Search Criteria"}
          />
          <Button 
            type="button" 
            variant="secondary"
            size="lg"
            label="Clear Form"
            onClick={handleReset}
          />
          {isEditMode && (
            <Button 
              type="button" 
              variant="secondary"
              size="lg"
              label="New Search"
              onClick={handleNewSearch}
            />
          )}
        </div>
      </form>

      {message.visible && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}
    </div>
  );
};

export default RegisterForm;
